import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Briefcase, Wallet, Clock, Filter, X, Building2, CalendarClock } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const LOCATIONS = ["all", "Bengaluru", "Pune", "Mumbai", "Hyderabad", "Chennai", "Delhi NCR", "Ahmedabad", "Remote"];
const JOB_TYPES = ["all", "Full Time", "Part Time", "Remote", "Internship"];
const CATEGORIES = ["all", "Engineering", "Design", "Marketing", "Sales", "Analytics", "Human Resources", "Operations", "Finance"];
const EXPERIENCE = ["all", "0-1", "1-3", "2-4", "3-5", "5-8", "8+"];

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [location, setLocation] = useState(params.get("location") || "all");
  const [jobType, setJobType] = useState(params.get("job_type") || "all");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [experience, setExperience] = useState(params.get("experience") || "all");

  const filters = useMemo(
    () => ({ search, location, job_type: jobType, category, experience }),
    [search, location, jobType, category, experience]
  );

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api
      .get("/jobs", { params: filters, signal: controller.signal })
      .then(({ data }) => setJobs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [filters]);

  const activeFilters = [
    location !== "all" && { key: "location", label: location, clear: () => setLocation("all") },
    jobType !== "all" && { key: "type", label: jobType, clear: () => setJobType("all") },
    category !== "all" && { key: "cat", label: category, clear: () => setCategory("all") },
    experience !== "all" && { key: "exp", label: `${experience} yrs`, clear: () => setExperience("all") },
  ].filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Open Roles</p>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
          Discover roles that match your ambition.
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Search across verified companies, filter by role, salary and location — and apply in one click.
        </p>
      </div>

      {/* Search bar */}
      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-4 lg:p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="jobs-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by role, company, or skill…"
              className="pl-10 h-11 rounded-xl border-slate-200 focus-visible:ring-indigo-500"
            />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger data-testid="filter-location" className="h-11 rounded-xl border-slate-200"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l === "all" ? "All locations" : l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger data-testid="filter-job-type" className="h-11 rounded-xl border-slate-200"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent>{JOB_TYPES.map((l) => <SelectItem key={l} value={l}>{l === "all" ? "All types" : l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className="h-11 rounded-xl border-slate-200"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((l) => <SelectItem key={l} value={l}>{l === "all" ? "All categories" : l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={experience} onValueChange={setExperience}>
              <SelectTrigger data-testid="filter-experience" className="h-11 rounded-xl border-slate-200"><SelectValue placeholder="Experience" /></SelectTrigger>
              <SelectContent>{EXPERIENCE.map((l) => <SelectItem key={l} value={l}>{l === "all" ? "Any experience" : `${l} years`}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Filter size={13} /> Active</span>
            {activeFilters.map((f) => (
              <button key={f.key} onClick={f.clear} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                {f.label} <X size={12} />
              </button>
            ))}
            <button
              data-testid="filter-reset"
              onClick={() => { setLocation("all"); setJobType("all"); setCategory("all"); setExperience("all"); setSearch(""); }}
              className="text-xs text-slate-500 hover:text-slate-800 ml-1 underline underline-offset-2"
            >
              Reset all
            </button>
          </div>
        )}
      </div>

      {/* Job list */}
      <div className="mt-8">
        <p className="text-sm text-slate-500 mb-4"><span className="font-semibold text-slate-900">{jobs.length}</span> roles found</p>

        {loading ? (
          <div className="grid gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-200 rounded-2xl">
            <p className="text-slate-500">No jobs match those filters yet. Try broadening your search.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job, i) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                data-testid={`job-card-${job.id}`}
                className="card-hover bg-white border border-slate-200 rounded-2xl p-6"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                  <div className="w-14 h-14 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                    {job.logo_path ? <img data-testid={`job-logo-${job.id}`} src={`${BACKEND_URL}/api/logos/${job.logo_path}`} alt={job.company} className="w-full h-full object-contain" /> : <Building2 size={22} className="text-slate-400" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-slate-900 font-display">{job.title}</h3>
                      <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border-0">{job.employment_type}</Badge>
                      <Badge variant="outline" className="border-slate-200 text-slate-600">{job.category}</Badge>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-500">{job.company}</p>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{job.description}</p>
                    <div className="mt-4 flex items-center gap-5 gap-y-2 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1.5"><MapPin size={13} className="text-indigo-500" /> {job.location}</span>
                      <span className="flex items-center gap-1.5"><Wallet size={13} className="text-indigo-500" /> {job.salary_range}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} className="text-indigo-500" /> {job.experience}</span>
                      <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-indigo-500" /> {job.employment_type}</span>
                      {job.apply_by && <span data-testid={`job-deadline-${job.id}`} className="flex items-center gap-1.5 text-amber-700 font-medium"><CalendarClock size={13} /> Apply by {new Date(job.apply_by).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Link to={`/enquiry?job=${encodeURIComponent(job.title)}`}>
                      <Button data-testid={`apply-btn-${job.id}`} className="rounded-full px-6 h-11 bg-gradient-to-r from-blue-700 to-indigo-600 hover:from-blue-800 hover:to-indigo-700 text-white font-semibold shadow-md shadow-indigo-500/20">
                        Apply Now
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
